// src/hooks/useThreadStore.js
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

const useThreadStore = () => {
  const [threads, setThreads] = useState([]);
  const [thread, setThread] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Use a ref to track if component is mounted
  const mountedRef = useRef(true);
  
  // Set up mounted ref
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  
  // Fetch threads based on category and page
  const fetchThreads = useCallback(async (reset = false) => {
    if (!mountedRef.current) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const currentPage = reset ? 1 : page;
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', 10);
      if (category !== 'all') {
        params.append('category', category);
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/threads?${params.toString()}`);
      
      if (response.data.success && mountedRef.current) {
        if (reset) {
          setThreads(response.data.data);
          setPage(1);
        } else {
          setThreads(prev => [...prev, ...response.data.data]);
        }
        
        setTotal(response.data.total);
        setHasMore(currentPage < response.data.totalPages);
      }
    } catch (err) {
      console.error('Error fetching threads:', err);
      if (mountedRef.current) {
        setError('Failed to load threads. Please try again later.');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [category, page]);
  
  // Load initial threads
  useEffect(() => {
    fetchThreads(true);
  }, [category, fetchThreads]);
  
  // Load more threads when page changes
  useEffect(() => {
    if (page > 1) {
      fetchThreads();
    }
  }, [page, fetchThreads]);
  
  // Change category
  const changeCategory = useCallback((newCategory) => {
    if (newCategory !== category) {
      setCategory(newCategory);
      // Reset will be triggered by the useEffect
    }
  }, [category]);
  
  // Load more threads
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      setPage(prevPage => prevPage + 1);
    }
  }, [loading, hasMore]);
  
  // Fetch a single thread by ID
  const fetchThread = useCallback(async (threadId) => {
    if (!mountedRef.current) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API_BASE_URL}/api/threads/${threadId}`);
      
      if (response.data.success && mountedRef.current) {
        setThread(response.data.data.thread);
        setComments(response.data.data.comments);
      }
    } catch (err) {
      console.error('Error fetching thread:', err);
      if (mountedRef.current) {
        setError('Failed to load thread. Please try again later.');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);
  
  // Create a new thread
  const createThread = useCallback(async (threadData) => {
    if (!mountedRef.current) return null;
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_BASE_URL}/api/threads`,
        threadData,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        return response.data.data;
      }
    } catch (err) {
      console.error('Error creating thread:', err);
      throw new Error(err.response?.data?.error || 'Failed to create thread');
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);
  
  // Add a comment to a thread
  const addComment = useCallback(async (threadId, content, parentId = null) => {
    if (!mountedRef.current) return null;
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_BASE_URL}/api/comments/thread/${threadId}`,
        { content, parentId },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.data.success && mountedRef.current) {
        const newComment = response.data.data;
        
        // If it's a reply to an existing comment
        if (parentId) {
          setComments(prevComments => 
            prevComments.map(comment => {
              if (comment._id === parentId) {
                return {
                  ...comment,
                  replies: [...(comment.replies || []), newComment]
                };
              }
              return comment;
            })
          );
        } else {
          // It's a top-level comment
          setComments(prevComments => [...prevComments, {
            ...newComment,
            replies: []
          }]);
        }
        
        return newComment;
      }
    } catch (err) {
      console.error('Error adding comment:', err);
      throw new Error(err.response?.data?.error || 'Failed to add comment');
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);
  
  // Update a comment
  const updateComment = useCallback(async (commentId, content) => {
    if (!mountedRef.current) return null;
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      
      const response = await axios.patch(
        `${API_BASE_URL}/api/comments/${commentId}`,
        { content },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.data.success && mountedRef.current) {
        const updatedComment = response.data.data;
        
        // Find if it's a top-level comment or a reply
        let isReply = false;
        let parentId = null;
        
        comments.forEach(comment => {
          if (comment._id === commentId) {
            isReply = false;
          } else if (comment.replies) {
            comment.replies.forEach(reply => {
              if (reply._id === commentId) {
                isReply = true;
                parentId = comment._id;
              }
            });
          }
        });
        
        if (isReply && parentId) {
          setComments(prevComments => 
            prevComments.map(comment => {
              if (comment._id === parentId) {
                return {
                  ...comment,
                  replies: comment.replies.map(reply => 
                    reply._id === commentId ? updatedComment : reply
                  )
                };
              }
              return comment;
            })
          );
        } else {
          setComments(prevComments => 
            prevComments.map(comment => 
              comment._id === commentId ? { ...updatedComment, replies: comment.replies } : comment
            )
          );
        }
        
        return updatedComment;
      }
    } catch (err) {
      console.error('Error updating comment:', err);
      throw new Error(err.response?.data?.error || 'Failed to update comment');
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [comments]);
  
  // Delete a comment
  const deleteComment = useCallback(async (commentId) => {
    if (!mountedRef.current) return false;
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      
      const response = await axios.delete(
        `${API_BASE_URL}/api/comments/${commentId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.data.success && mountedRef.current) {
        // Find if it's a top-level comment or a reply
        let isReply = false;
        let parentId = null;
        
        comments.forEach(comment => {
          if (comment._id === commentId) {
            isReply = false;
          } else if (comment.replies) {
            comment.replies.forEach(reply => {
              if (reply._id === commentId) {
                isReply = true;
                parentId = comment._id;
              }
            });
          }
        });
        
        if (isReply && parentId) {
          setComments(prevComments => 
            prevComments.map(comment => {
              if (comment._id === parentId) {
                return {
                  ...comment,
                  replies: comment.replies.filter(reply => reply._id !== commentId)
                };
              }
              return comment;
            })
          );
        } else {
          setComments(prevComments => 
            prevComments.filter(comment => comment._id !== commentId)
          );
        }
        
        return true;
      }
    } catch (err) {
      console.error('Error deleting comment:', err);
      throw new Error(err.response?.data?.error || 'Failed to delete comment');
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [comments]);
  
  // Safe reset function that checks if component is mounted
  const reset = useCallback(() => {
    if (mountedRef.current) {
      setThread(null);
      setComments([]);
      setError(null);
    }
  }, []);
  
  return {
    // State
    threads,
    thread,
    comments,
    loading,
    error,
    category,
    hasMore,
    total,
    
    // Actions
    fetchThreads,
    fetchThread,
    createThread,
    addComment,
    updateComment,
    deleteComment,
    changeCategory,
    loadMore,
    
    // Reset state when navigating away
    reset
  };
};

export default useThreadStore;